// Experience catalog of the AI-first Studio (Creative Studio 2.0): the kinds of
// creative a person can ask the AI for, filtered by the creative's product
// (`doc.meta.intendedProduct`). Pure data + pure checks — no React, no DOM.
//
// Rule: the catalog only offers what ACTUALLY works end to end. A game kind is
// offered only when its starter (the same `gameStarter` the AI and the Studio
// use) builds a valid document whose every format the SERVED runtime executes
// without gaps (`documentRuntimeGaps`, the same check as the delivery plane,
// ADR-0029). Choosing an experience only fills the prompt: nothing is created
// until the person sends it.
import {
  applyAndValidate,
  createSkeletonDocument,
  gameStarter,
  GAME_STARTER_KINDS,
  type CreativeDocument,
  type GameStarterKind,
} from "@interactads/creative-model";
import { documentRuntimeGaps } from "@interactads/creative-runtime/support";
import { addVariant } from "./commands";
import { layoutSize } from "./model";

/** Closed set of icon names; the UI maps them to its icon components. */
export type ExperienceIcon =
  "banner" | "carousel" | "video" | "survey" | "find" | "pairs" | "tap" | "quiz" | "drag" | "runner";

export type ExperienceProduct = CreativeDocument["meta"]["intendedProduct"];

export interface Experience {
  readonly id: string;
  readonly product: ExperienceProduct;
  readonly name: string;
  /** One line, customer language. */
  readonly description: string;
  readonly icon: ExperienceIcon;
  /** Prompt the person completes: the bracketed parts are theirs to fill in. */
  readonly prompt: string;
  /** Needs a media file in the brand library first (the AI never generates video). */
  readonly requires?: "video";
  /** For advergames: the starter the AI builds from. */
  readonly game?: GameStarterKind;
}

const GAME_EXPERIENCES: Record<GameStarterKind, Omit<Experience, "id" | "product" | "game">> = {
  find: {
    name: "Encuentra las diferencias",
    description: "La persona toca las diferencias escondidas en la imagen.",
    icon: "find",
    prompt:
      "Un juego de «Encuentra las diferencias» para [tu marca]: la persona busca [cuántas] diferencias en una imagen de [producto o escena] y al final ve un botón «[texto del botón]».",
  },
  pairs: {
    name: "Memoria (parejas)",
    description: "Voltea cartas y encuentra las parejas.",
    icon: "pairs",
    prompt:
      "Un juego de memoria para [tu marca] con [cuántas] parejas de cartas que muestran [productos o íconos de la marca]; al ganar aparece «[mensaje final]» y un botón «[texto del botón]».",
  },
  tap: {
    name: "Atrápalo",
    description: "Toca el objetivo antes de que se escape y suma puntos.",
    icon: "tap",
    prompt:
      "Un juego de «Atrápalo» para [tu marca]: la persona toca [el objetivo] para sumar puntos antes de que termine el tiempo; al final, botón «[texto del botón]».",
  },
  quiz: {
    name: "Quiz",
    description: "Preguntas con respuesta correcta y puntaje.",
    icon: "quiz",
    prompt:
      "Un quiz de [cuántas] preguntas sobre [tema] para [tu marca], con la respuesta correcta marcada y un mensaje final con botón «[texto del botón]».",
  },
  drag: {
    name: "Arrastra al lugar",
    description: "Lleva cada pieza a su caja.",
    icon: "drag",
    prompt:
      "Un juego para [tu marca] donde la persona arrastra [las piezas] hasta [su lugar correcto]; al completarlo ve «[mensaje final]» y un botón «[texto del botón]».",
  },
  runner: {
    name: "Carrera (plataformas)",
    description: "El personaje corre, salta, esquiva peligros y recoge energía.",
    icon: "runner",
    prompt:
      "Un juego de carrera para [tu marca]: [el personaje] corre por [ambientes, por ejemplo la ciudad y la playa], salta [los obstáculos] y recoge [los premios]; al llegar a la meta, botón «[texto del botón]».",
  },
};

/** Every experience the Studio knows, before filtering. Order = display order. */
export const EXPERIENCES: readonly Experience[] = [
  {
    id: "banner",
    product: "RICH_MEDIA_STANDARD",
    name: "Banner animado",
    description: "Producto, titular y botón con animaciones de entrada.",
    icon: "banner",
    prompt:
      "Un banner para [tu marca] con [el producto], el titular «[titular]» y un botón «[texto del botón]» que lleve a [sitio web].",
  },
  {
    id: "carousel",
    product: "RICH_MEDIA_STANDARD",
    name: "Carrusel",
    description: "Varias diapositivas que la persona desliza, con botón a la tienda.",
    icon: "carousel",
    prompt:
      "Un carrusel de [cuántas] diapositivas para [tu marca] que muestre [beneficios o productos], cada una con una imagen y un texto corto, y un botón «[texto del botón]».",
  },
  {
    id: "video-banner",
    product: "VIDEO_RICH_MEDIA",
    name: "Banner con video",
    description: "Tu video de la biblioteca con titular y botón (empieza sin sonido).",
    icon: "video",
    requires: "video",
    prompt:
      "Un banner con el video [nombre del video] de la biblioteca para [tu marca], el titular «[titular]» y un botón «[texto del botón]».",
  },
  {
    id: "survey",
    product: "SURVEY_RICH_MEDIA",
    name: "Encuesta ramificada",
    description: "Preguntas de respuesta cerrada que cambian según lo que se elige. Sin datos personales.",
    icon: "survey",
    prompt:
      "Una encuesta corta para [tu marca] sobre [tema], con [cuántas, hasta 8] preguntas de respuesta cerrada; si la persona responde [una opción], pregúntale [otra cosa]. Al final, un mensaje de agradecimiento.",
  },
  ...GAME_STARTER_KINDS.map((kind): Experience => ({
    id: `game-${kind}`,
    product: "ADVERGAMING",
    game: kind,
    ...GAME_EXPERIENCES[kind],
  })),
];

// ─── "Actually works" check for game starters ───────────────────────────────

const probeCache = new Map<string, boolean>();
const PROBE_CACHE_MAX = 64;

/** Fixed-size formats of the document (format key + size), the probe's layouts. */
function probeFormats(doc: CreativeDocument): { key: string; width: number; height: number }[] {
  return doc.layouts.flatMap((l) => {
    const key = l.formatKeys[0];
    return key ? [{ key, ...layoutSize(l) }] : [];
  });
}

/** A fresh, empty document with the same product and formats (the starter's real starting point). */
function probeBase(doc: CreativeDocument): CreativeDocument | null {
  const [first, ...rest] = probeFormats(doc);
  if (!first) return null;
  const skeleton = createSkeletonDocument({
    intendedProduct: doc.meta.intendedProduct,
    locale: doc.meta.locale,
    accessibilityLabel: "Prueba",
    format: first,
  });
  if (!skeleton.ok) return null;
  let base = skeleton.document;
  for (const f of rest) {
    const next = applyAndValidate(base, addVariant(base, f).ops).document;
    if (!next) return null;
    base = next;
  }
  return base;
}

/**
 * True when the `kind` starter builds a document the served runtime executes in every format of
 * `doc` with no gaps. Deterministic and cached by product + formats.
 */
export function gameStarterWorks(doc: CreativeDocument, kind: GameStarterKind): boolean {
  const formats = probeFormats(doc)
    .map((f) => `${f.key}:${String(f.width)}x${String(f.height)}`)
    .join(",");
  const cacheKey = `${doc.meta.intendedProduct}|${formats}|${kind}`;
  const cached = probeCache.get(cacheKey);
  if (cached !== undefined) return cached;
  let works = false;
  try {
    const base = probeBase(doc);
    if (base) {
      const built = applyAndValidate(base, gameStarter(base, kind).ops).document;
      works = built !== undefined && built.layouts.every((l) => documentRuntimeGaps(built, l.id).length === 0);
    }
  } catch {
    works = false;
  }
  if (probeCache.size >= PROBE_CACHE_MAX) probeCache.clear();
  probeCache.set(cacheKey, works);
  return works;
}

/** The experiences offered for this creative: its product only, and only what works end to end. */
export function experiencesFor(doc: CreativeDocument): Experience[] {
  return EXPERIENCES.filter(
    (x) => x.product === doc.meta.intendedProduct && (x.game === undefined || gameStarterWorks(doc, x.game))
  );
}

/** Where the person starts typing in a template: the first bracketed placeholder (or the end). */
export function firstPlaceholder(prompt: string): { start: number; end: number } {
  const start = prompt.indexOf("[");
  const end = start >= 0 ? prompt.indexOf("]", start) : -1;
  return start >= 0 && end > start ? { start, end: end + 1 } : { start: prompt.length, end: prompt.length };
}
